// Persisted user preferences for the terminal itself (font, scrollback,
// cursor, clipboard behavior). Lives apart from useAppStore — that store
// carries server-derived hosts and mock chat data that must never persist.
// terminalManager subscribes to this store and pushes changes into every
// live xterm instance.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CursorStyle = 'block' | 'bar' | 'underline';

// Desktop-only: how terminal bytes reach hosts. 'direct' (default) SSHes
// straight from the desktop app; 'auto' falls back to relay when direct
// fails; 'relay' always routes through the cotty server. Ignored on web
// (always relay — there is no local gateway).
export type DesktopConnectionMode = 'direct' | 'auto' | 'relay';

export const FONT_SIZE_MIN = 8;
export const FONT_SIZE_MAX = 24;
export const FONT_SIZE_DEFAULT = 13;

const clampFontSize = (px: number): number =>
    Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(px)));

interface SettingsStore {
    fontSize: number;
    scrollback: number;
    cursorStyle: CursorStyle;
    copyOnSelect: boolean;
    warnMultilinePaste: boolean;
    perfHudEnabled: boolean;
    desktopConnectionMode: DesktopConnectionMode;

    setFontSize: (px: number) => void;
    adjustFontSize: (delta: number) => void;
    resetFontSize: () => void;
    setScrollback: (lines: number) => void;
    setCursorStyle: (style: CursorStyle) => void;
    setCopyOnSelect: (on: boolean) => void;
    setWarnMultilinePaste: (on: boolean) => void;
    setPerfHudEnabled: (on: boolean) => void;
    togglePerfHud: () => void;
    setDesktopConnectionMode: (m: DesktopConnectionMode) => void;
}

export const useSettings = create<SettingsStore>()(
    persist(
        (set) => ({
            fontSize: FONT_SIZE_DEFAULT,
            scrollback: 10_000,
            cursorStyle: 'block',
            copyOnSelect: false,
            warnMultilinePaste: true,
            perfHudEnabled: false,
            desktopConnectionMode: 'direct',

            setFontSize: (px) => set({ fontSize: clampFontSize(px) }),
            adjustFontSize: (delta) =>
                set((s) => ({ fontSize: clampFontSize(s.fontSize + delta) })),
            resetFontSize: () => set({ fontSize: FONT_SIZE_DEFAULT }),
            setScrollback: (lines) => set({ scrollback: lines }),
            setCursorStyle: (style) => set({ cursorStyle: style }),
            setCopyOnSelect: (on) => set({ copyOnSelect: on }),
            setWarnMultilinePaste: (on) => set({ warnMultilinePaste: on }),
            setPerfHudEnabled: (on) => set({ perfHudEnabled: on }),
            togglePerfHud: () =>
                set((s) => ({ perfHudEnabled: !s.perfHudEnabled })),
            setDesktopConnectionMode: (m) =>
                set({ desktopConnectionMode: m }),
        }),
        {
            name: 'cotty-settings',
            version: 1,
            migrate: (persisted) => persisted as SettingsStore,
        },
    ),
);
